import office from '@/taskpane/officejs/office-wrapper'
import MailInfo from '@/taskpane/events/models/mail-info'
import { GraphApiCallType } from './graph-api-call-args'
import graphRequestAwaitCallback from './graph-api-call-await-callback'
import GraphEventInfo from './models/graph-event-info'
import GraphMessageInfo from './models/graph-message-info'
import PersonInfo from '../events/models/person-info'
import EventInfo from '../events/models/event-info'
import getTimeInfo from '../common/get-time-info'
import GraphAttachmentContents from './models/graph-attachment-contents'
import AttachmentInfo from '../events/models/attachment-info'
import AttachmentContents from '../events/models/attachment-contents'
import { EmailListItem } from '../events/models/email-list-item'
import { PartyDto, Party, SentEmailInfo } from '../events/models/sent-email-info'
import getRestItemId from '../office-rest/office-rest-get-item-id'
import { SaveEmailTrackingInfoArgs } from '../events/handlers/save-email-tracking-handler'
import configuration from '../common/configuration'
import { logErrorToApi } from '../common/error-logging'

function isGraphApiAvailable(): boolean {
  return !!Office.auth?.getAccessToken && office.profile.accountType() !== 'enterprise' && office.profile.accountType() !== 'gmail'
}

// Graph API requests are split in half so they can share extensive common token handling
// graph-wrapper creates payload and calls graphRequestAwaitCallback which calls graph-api-call-with-sso-wrapper
//   graph-api-call-with-sso-wrapper gets an access token, and handles front end token errors that require a login popup
//     graph-api-call-async makes the actual Graph Call with the token
//   graph-api-call-with-sso-wrapper handles errors from the Graph call, potentially requiring the secondary login popup
// graph-wrapper packages the Graph result into a return object

async function graphMailItem_GetInfo(id: string): Promise<MailInfo> {
  const mailbox = await getCurrentItemMailbox()
  const payload = {
    id: getRestItemId(id),
  }

  const mailInfo = (await graphRequestAwaitCallback(GraphApiCallType.GetMessageInfo, mailbox, payload)).data as GraphMessageInfo

  const bccRecipients = mailInfo.bccRecipients.map((bcc) => {
    return {
      displayName: bcc.emailAddress.name,
      emailAddress: bcc.emailAddress.address,
    } as PersonInfo
  })

  return {
    bcc: bccRecipients,
    datetime: mailInfo.sentDateTime ? new Date(mailInfo.sentDateTime) : null,
    threadId: mailInfo.conversationId.replace(/_/g, '+').replace(/-/g, '/'),
    threadIndex: mailInfo.conversationIndex || '',
    headerMessageId: mailInfo.internetMessageId,
  } as MailInfo
}

async function graphEventItem_GetInfo(id: string): Promise<EventInfo> {
  const mailbox = await getCurrentItemMailbox()
  const payload = {
    id: getRestItemId(id),
  }

  const eventInfo = (await graphRequestAwaitCallback(GraphApiCallType.GetEventInfo, mailbox, payload)).data as GraphEventInfo

  let start: Date
  let end: Date

  if (eventInfo.isAllDay) {
    start = new Date(eventInfo.start?.dateTime)
    end = new Date(eventInfo.end?.dateTime)
  } else {
    start = new Date(eventInfo.start?.dateTime + 'Z')
    end = new Date(eventInfo.end?.dateTime + 'Z')
  }

  return {
    time: getTimeInfo(start, end),
    uid: eventInfo.uid,
    iCalUId: eventInfo.iCalUId,
  } as EventInfo
}

async function graphMailItem_GetAttachment(id: string, attachment: AttachmentInfo): Promise<AttachmentContents> {
  const mailbox = await getCurrentItemMailbox()
  const payload = {
    id: getRestItemId(id),
    attachmentId: getRestItemId(attachment.id),
  }

  const contents = await graphRequestAwaitCallback(GraphApiCallType.GetMessageAttachment, mailbox, payload)

  return {
    ...attachment,
    contentsBase64: (contents.data as GraphAttachmentContents).contentBytes,
  } as AttachmentContents
}

async function graphEventItem_GetAttachment(id: string, attachment: AttachmentInfo): Promise<AttachmentContents> {
  const mailbox = await getCurrentItemMailbox()
  const payload = {
    id: getRestItemId(id),
    attachmentId: getRestItemId(attachment.id),
  }

  const contents = await graphRequestAwaitCallback(GraphApiCallType.GetEventAttachment, mailbox, payload)

  return {
    ...attachment,
    contentsBase64: (contents.data as GraphAttachmentContents).contentBytes,
  } as AttachmentContents
}

async function graphMailItem_SetMailCustomProperties(id: string, keyValuePairs: { [key: string]: string }) {
  const mailbox = await getCurrentItemMailbox()
  const payload = {
    id: id,
    extendedProperties: keyValuePairs,
  }

  const result = await graphRequestAwaitCallback(GraphApiCallType.SetMailCustomProperties, mailbox, payload)
  return result
}

// The sent-items query expands two unrelated extended properties into one flat array: the
// Cirrus custom-property blob (JSON written by Office.js CustomProperties) and String 0x007D
// / PR_TRANSPORT_MESSAGE_HEADERS (a raw MIME header block). Graph does not guarantee an order,
// and untracked mail carries only the headers, so the blob has to be selected by property id -
// reading index 0 threw a SyntaxError that aborted the entire poll.
//
// The $expand in graph-api-call-async restricts the response to exactly those two ids, so the
// only cecp- property that can reach this function is the one written under our own
// MANIFEST_APP_ID. Selection and parsing both sit inside the try because this runs per message
// inside an unguarded .map(), where anything thrown would abort the whole poll again.
function parseCirrusCustomProperties(item: SentEmailInfo): SaveEmailTrackingInfoArgs | null {
  const properties = item.singleValueExtendedProperties ?? []

  try {
    const propertyName = `name cecp-${configuration.MANIFEST_APP_ID}`.toLowerCase()
    const match = properties.find((property) => property.id.toLowerCase().includes(propertyName))

    if (!match?.value) return null

    return JSON.parse(match.value) as SaveEmailTrackingInfoArgs
  } catch (error) {
    // One malformed blob must not take down the poll. Dropping the ids here leaves the item to
    // match on messageId / headerMessageId / thread pair instead.
    console.error('graph-wrapper: failed to parse Cirrus custom properties', { id: item.id, error })
    logErrorToApi('Sent Items Custom Property Parse', 'Failed to parse Cirrus custom properties', error, undefined, {
      id: item.id,
      propertyIds: properties.map((property) => property.id),
    })
    return null
  }
}

async function graphMailItem_GetSentEmailsToProcess(queryFromDateTime: string): Promise<EmailListItem[]> {
  const mailbox = await getCurrentItemMailbox()
  const payload = {
    mailbox: mailbox,
    ...(queryFromDateTime && { lastProcessedDateTime: queryFromDateTime }),
  }

  const result = await graphRequestAwaitCallback(GraphApiCallType.GetSentEmailsToProcess, mailbox, payload)
  if (result.data?.value) {
    const parsed = result.data.value.map((item: SentEmailInfo) => {
      const customProperties = parseCirrusCustomProperties(item)

      return {
        id: item.id.replace(/_/g, '+').replace(/-/g, '/'),
        threadId: item.conversationId.replace(/_/g, '+').replace(/-/g, '/'),
        threadIndex: item.conversationIndex,
        headerMessageId: item.internetMessageId,
        sentTime: new Date(item.sentDateTime),
        subject: item.subject,
        sentBy: { displayName: item.sender.emailAddress.name, emailAddress: item.sender.emailAddress.address } as PartyDto,
        toRecipients: item.toRecipients.map((r: Party) => ({ displayName: r.emailAddress.name, emailAddress: r.emailAddress.address }) as PartyDto),
        ccRecipients: item.ccRecipients.map((r: Party) => ({ displayName: r.emailAddress.name, emailAddress: r.emailAddress.address }) as PartyDto),
        bccRecipients: item.bccRecipients.map((r: Party) => ({ displayName: r.emailAddress.name, emailAddress: r.emailAddress.address }) as PartyDto),
        extendedProperties: {
          replyTrackingId: customProperties?.replyTrackingId,
          emailGuid: customProperties?.emailGuid,
        },
      } as EmailListItem
    })
    return parsed
  }

  return []
}

async function getCurrentItemMailbox(): Promise<string> {
  const targetMailbox = await office.mail.targetMailbox()
  return targetMailbox
}

function isPrimaryMailboxContext(targetMailbox: string): boolean {
  const primary = (office.profile.username() || '').trim().toLowerCase()
  const target = (targetMailbox || '').trim().toLowerCase()
  if (!target) {
    return true
  }
  if (!primary) {
    return false
  }
  return target === primary
}

async function graphTranslateExchangeIds(
  inputIds: string[],
  sourceIdType: string = 'restImmutableEntryId',
  targetIdType: string = 'restId',
): Promise<string[]> {
  const mailbox = await getCurrentItemMailbox()
  if (!isPrimaryMailboxContext(mailbox)) {
    return []
  }
  const payload = {
    inputIds: inputIds,
    sourceIdType: sourceIdType,
    targetIdType: targetIdType,
  }

  const result = await graphRequestAwaitCallback(GraphApiCallType.TranslateExchangeIds, mailbox, payload)
  if (result.data?.value && result.data.value.length > 0) {
    return result.data.value.map((item: { sourceId: string; targetId: string }) => item.targetId)
  }
  return []
}

const graph = {
  isAvailable: isGraphApiAvailable,
  getMailInfo: graphMailItem_GetInfo,
  getEventInfo: graphEventItem_GetInfo,
  setMailCustomProperties: graphMailItem_SetMailCustomProperties,
  getSentEmailsToProcess: graphMailItem_GetSentEmailsToProcess,
  getMailAttachment: graphMailItem_GetAttachment,
  getEventAttachment: graphEventItem_GetAttachment,
  translateExchangeIds: graphTranslateExchangeIds,
}

export default graph
